/**
 * Dashboard owns the appearance preference (ENTUC-7893, `next-themes`) and
 * expresses it as a `.dark` class on its own <html>. Connect runs in an
 * iframe, so that class never reaches us. Dashboard hands the preference
 * across the boundary twice: as `?theme=` on the iframe URL so the first paint
 * is already right, and as a `setTheme` postMessage so a Profile change (or an
 * OS change under "system") updates a modal that is already open.
 *
 * Connect keeps no preference of its own and only toggles the design-system
 * `.dark` class on its <html> (ENTUC-7905).
 */
export const themePreferences = ['light', 'dark', 'system'] as const;
export type ThemePreference = (typeof themePreferences)[number];
export type ResolvedTheme = Exclude<ThemePreference, 'system'>;

export const THEME_PARAM = 'theme';
export const SET_THEME_MESSAGE_TYPE = 'setTheme';

const DARK_CLASS = 'dark';
const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';
const DEFAULT_PREFERENCE: ThemePreference = 'light';

const isThemePreference = (value: unknown): value is ThemePreference =>
  themePreferences.some(preference => preference === value);

export const parseThemePreference = (value: unknown): ThemePreference | undefined =>
  isThemePreference(value) ? value : undefined;

export const resolveTheme = (preference: ThemePreference, prefersDark: boolean): ResolvedTheme => {
  if (preference === 'system') return prefersDark ? 'dark' : 'light';
  return preference;
};

export const applyTheme = (
  theme: ResolvedTheme,
  root: HTMLElement = document.documentElement
): void => {
  root.classList.toggle(DARK_CLASS, theme === 'dark');
  // The head script sets an inline color-scheme so the first paint is right.
  // That attribute beats `html.dark { color-scheme }` in style.css, which is
  // only the JS-off fallback. A later switch has to update this inline style
  // or native controls and scrollbars stay on the first scheme.
  root.style.colorScheme = theme;
};

type SetThemeMessage = { type: typeof SET_THEME_MESSAGE_TYPE; payload?: { theme?: unknown } };

const isSetThemeMessage = (data: unknown): data is SetThemeMessage =>
  typeof data === 'object' &&
  data !== null &&
  (data as { type?: unknown }).type === SET_THEME_MESSAGE_TYPE;

/**
 * Applies the initial theme synchronously and keeps it in sync for the life of
 * the page. Call before the first render. Returns a teardown for tests.
 */
export const initThemeBridge = (win: Window = window): (() => void) => {
  const params = new URLSearchParams(win.location.search);
  // Same `origin` the callbacks post to; it is also the only page allowed to restyle us.
  const parentOrigin = params.get('origin');
  const darkScheme = win.matchMedia(DARK_SCHEME_QUERY);

  let preference = parseThemePreference(params.get(THEME_PARAM)) ?? DEFAULT_PREFERENCE;

  const render = () =>
    applyTheme(resolveTheme(preference, darkScheme.matches), win.document.documentElement);

  const handleSchemeChange = () => {
    if (preference === 'system') render();
  };

  const handleMessage = (event: MessageEvent) => {
    if (!parentOrigin || event.origin !== parentOrigin) return;
    if (!isSetThemeMessage(event.data)) return;

    const next = parseThemePreference(event.data.payload?.theme);
    if (!next) return;

    preference = next;
    render();
  };

  render();
  darkScheme.addEventListener('change', handleSchemeChange);
  win.addEventListener('message', handleMessage);

  return () => {
    darkScheme.removeEventListener('change', handleSchemeChange);
    win.removeEventListener('message', handleMessage);
  };
};
